/**
 * Preview Mode — a no-Firebase, no-Helcim, fully-mocked path so you can browse
 * the customer portal and subscription checkout without provisioning anything.
 *
 * Activate by setting `VITE_PREVIEW_MODE=1` in apps/web/.env.local (or by
 * running `npm run dev:preview` once that script is in place).
 *
 * What changes when preview mode is on:
 *   - The auth store fakes a signed-in user with accountId = 'demo-acct-1'.
 *   - The accounts store returns a seeded demo Account doc immediately.
 *   - swaps/invoices/manifests stores return realistic mock collections.
 *   - The subscription-checkout flow short-circuits the Cloud Function call
 *     and pretends it succeeded.
 *   - A floating "DEMO MODE" badge is visible so it's always obvious.
 *
 * What stays real:
 *   - Routing, components, styling, validation, the form UX — everything you
 *     actually want to QA on the UI side.
 */

import type {
  Account, Swap, Invoice, Manifest,
} from './types';

export function isPreviewMode(): boolean {
  // Treat any truthy value as on, including '1', 'true', 'yes'.
  const v = import.meta.env.VITE_PREVIEW_MODE;
  return v === '1' || v === 'true' || v === 'yes';
}

/** Minimal Timestamp-like used in preview-mode fixtures. The real Firestore
 *  Timestamp has more methods, but everything in the UI only calls .toDate()
 *  or accesses .seconds. We satisfy both. */
function fakeTs(date: Date | number): { toDate: () => Date; seconds: number; nanoseconds: number } {
  const d = date instanceof Date ? date : new Date(date);
  return {
    toDate: () => d,
    seconds: Math.floor(d.getTime() / 1000),
    nanoseconds: 0,
  };
}

const NOW = Date.now();
const DAY = 86400_000;

/* ============ FIXTURES ============ */

export const DEMO_ACCOUNT_ID = 'demo-acct-1';

export function demoAccount(): Account {
  return {
    id: DEMO_ACCOUNT_ID,
    accountType: 'practice',
    status: 'active',
    signupDate: fakeTs(NOW - 90 * DAY) as never,
    cancelDate: null,
    primaryContact: {
      name: 'Dr. Sarah Patel',
      email: 'sarah@maplegrovedental.com',
      phone: '+16145551234',
    },
    address: {
      line1: '4720 N High St',
      line2: 'Suite 200',
      city: 'Columbus',
      state: 'OH',
      zip: '43214',
      lat: 40.0421,
      lng: -83.0143,
      isColumbusMetro: true,
    },
    homeInfo: null,
    practiceInfo: {
      practiceName: 'Maple Grove Dental',
      practiceType: 'dental',
      providerCount: 3,
      volumeBucket: 'moderate',
      oshaAcknowledged: true,
      exposureControlPlanNeeded: false,
      secondaryContacts: [
        { name: 'Jen Morales (office mgr)', email: 'jen@maplegrovedental.com', phone: '+16145559876' },
      ],
    },
    businessInfo: null,
    helcimCustomerId: 'devstub_cust_demo',
    helcimCardToken: 'devstub_token_demo',
    cardLast4: '4242',
    subscriptionTier: 'practice_core',
    monthlyBaseFee: 1500,
    perSwapFee: 7900,
    containersInField: 1,
    notes: '',
    referralCode: 'MAPLE2026',
    referredBy: null,
    convertedFromOrderId: null,
    createdAt: fakeTs(NOW - 90 * DAY) as never,
    updatedAt: fakeTs(NOW - 2 * DAY) as never,
  };
}

export function demoSwaps(): Swap[] {
  const base = (i: number, status: Swap['status'], offsetDays: number): Swap => ({
    id: `demo-swap-${i}`,
    accountId: DEMO_ACCOUNT_ID,
    accountType: 'practice',
    requestedAt: fakeTs(NOW - offsetDays * DAY) as never,
    requestedVia: 'portal',
    scheduledWindow: status === 'scheduled' ? {
      start: fakeTs(NOW + 1 * DAY) as never,
      end: fakeTs(NOW + 2 * DAY) as never,
    } : null,
    status,
    urgent: false,
    fulfillmentMethod: 'columbus_pickup',
    assignedDriver: status === 'completed' ? 'Tony (operator)' : null,
    completedAt: status === 'completed' ? fakeTs(NOW - (offsetDays - 1) * DAY) as never : null,
    containerSize: '1.2gal',
    chargeId: status === 'completed' ? `inv-${i}` : null,
    chargeAmount: status === 'completed' ? 7900 : 0,
    manifestId: status === 'completed' ? `demo-manifest-${i}` : null,
    manifestUrl: status === 'completed' ? `manifest://demo/${i}` : null,
    mailbackPartner: 'pureway',
    partnerTrackingNumber: status === 'completed' ? `1Z999AA1${10000000 + i}` : null,
    treatmentDestructionCertId: status === 'manifested' ? `CERT-${i}-2026` : null,
    notes: '',
  });
  return [
    base(1, 'scheduled', 0),     // upcoming
    base(2, 'completed', 30),    // last month
    base(3, 'completed', 65),    // two months ago
    base(4, 'manifested', 95),   // first swap
  ];
}

export function demoInvoices(): Invoice[] {
  const mk = (i: number, type: Invoice['type'], amount: number, daysAgo: number, status: Invoice['status'] = 'paid'): Invoice => ({
    id: `inv-${i}`,
    accountId: DEMO_ACCOUNT_ID,
    orderId: null,
    helcimTransactionId: `devstub_tx_${i}`,
    type,
    amount,
    status,
    retryCount: 0,
    createdAt: fakeTs(NOW - daysAgo * DAY) as never,
    paidAt: status === 'paid' ? fakeTs(NOW - daysAgo * DAY) as never : null,
  });
  return [
    mk(1, 'setup', 2500, 90),
    mk(2, 'monthly_base', 1500, 90),
    mk(3, 'monthly_base', 1500, 60),
    mk(4, 'swap', 7900, 65),
    mk(5, 'monthly_base', 1500, 30),
    mk(6, 'swap', 7900, 30),
  ];
}

export function demoManifests(): Manifest[] {
  return [
    {
      id: 'demo-manifest-2',
      swapId: 'demo-swap-2',
      orderId: null,
      accountId: DEMO_ACCOUNT_ID,
      accountType: 'practice',
      generatedAt: fakeTs(NOW - 28 * DAY) as never,
      signedByAccount: true,
      signedByOperator: true,
      pdfUrl: 'manifest://demo/2',
      chainOfCustody: [
        { event: 'container_swapped', timestamp: fakeTs(NOW - 29 * DAY) as never, actor: 'Tony (operator)' },
        { event: 'manifest_generated', timestamp: fakeTs(NOW - 28 * DAY) as never, actor: 'system' },
        { event: 'mailback_received', timestamp: fakeTs(NOW - 25 * DAY) as never, actor: 'PureWay' },
        { event: 'destroyed', timestamp: fakeTs(NOW - 22 * DAY) as never, actor: 'PureWay' },
      ],
    },
    {
      id: 'demo-manifest-3',
      swapId: 'demo-swap-3',
      orderId: null,
      accountId: DEMO_ACCOUNT_ID,
      accountType: 'practice',
      generatedAt: fakeTs(NOW - 63 * DAY) as never,
      signedByAccount: true,
      signedByOperator: true,
      pdfUrl: 'manifest://demo/3',
      chainOfCustody: [
        { event: 'container_swapped', timestamp: fakeTs(NOW - 64 * DAY) as never, actor: 'Tony (operator)' },
        { event: 'manifest_generated', timestamp: fakeTs(NOW - 63 * DAY) as never, actor: 'system' },
        { event: 'destroyed', timestamp: fakeTs(NOW - 58 * DAY) as never, actor: 'PureWay' },
      ],
    },
  ];
}

/** Inserts a freshly-created swap into the in-memory fixture so the
 *  RequestSwap view can echo it back to the dashboard during preview testing. */
const inFlightSwaps: Swap[] = [];
export function recordPreviewSwap(s: Partial<Swap>): Swap {
  const id = `demo-swap-new-${inFlightSwaps.length + 1}`;
  const swap: Swap = {
    id,
    accountId: DEMO_ACCOUNT_ID,
    accountType: 'practice',
    requestedAt: fakeTs(Date.now()) as never,
    requestedVia: 'portal',
    scheduledWindow: null,
    status: 'requested',
    urgent: false,
    fulfillmentMethod: 'columbus_pickup',
    assignedDriver: null,
    completedAt: null,
    containerSize: '1.2gal',
    chargeId: null,
    chargeAmount: 0,
    manifestId: null,
    manifestUrl: null,
    mailbackPartner: null,
    partnerTrackingNumber: null,
    treatmentDestructionCertId: null,
    notes: '',
    ...s,
  };
  inFlightSwaps.push(swap);
  return swap;
}
export function previewSwaps(): Swap[] {
  return [...inFlightSwaps, ...demoSwaps()];
}
